import {
  AlertDescription,
  Button,
  Heading,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import {
  error as commonError,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { useGetBbplMonitorStatusQuery } from '../bbplMonitor/requests/bbplMonitor.gql.generated';
import { Alert } from '../common-frontend/components/Alert';
import { InfoAlertWithDescription } from '../common-frontend/components/InfoAlertWithDescription';
import {
  useAsyncOperationStatusQuery,
  useUndoPipelineRunMutation,
} from './requests/undoPipelineRun.gql.generated';

export const UndoPipelineRunView = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  const toast = useToast();

  const { data: bbplMonitorStatusData } = useGetBbplMonitorStatusQuery({
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityId,
    },
    onError: (error) => {
      // only toast on stage + dev
      if (!commonUtils.EnvUtils.isProduction()) {
        const errorMessage = commonError.getErrorMessage(error);
        toast({
          title: 'Error fetching BBPL Monitor status',
          status: 'error',
          description: errorMessage,
          isClosable: true,
        });
      }
    },
  });
  const latestInstance =
    bbplMonitorStatusData?.bbplMonitorStatus.latestArtifactInstance;
  const reportingDate = latestInstance?.reportingDate;
  const fileVersion = latestInstance?.fileVersion;

  const [undoPipelineRun] = useUndoPipelineRunMutation();

  const [state, setState] = useState<{
    reportingDate: Util.FullDateStr;
    fileVersion: number;
    operationId: string;
    status: string;
  }>();
  const { data } = useAsyncOperationStatusQuery({
    pollInterval: 5_000,
    skip: !state || state.status !== 'inProgress',
    variables: {
      operationId: state?.operationId!,
    },
  });
  useEffect(() => {
    setState((prevState) => {
      const newStatus = data?.asyncOperationStatus.response;
      if (newStatus && prevState && prevState.status !== newStatus) {
        return { ...prevState, status: newStatus };
      }
      return prevState;
    });
  }, [data?.asyncOperationStatus.response]);

  return (
    <>
      <InfoAlertWithDescription description="This is an unsafe operation that currently lacks validations. Proceed with caution!" />
      <Heading fontSize="m" mt="20px" mb="20px">
        Latest pipeline run
      </Heading>
      {(() => {
        if (state) {
          return (
            <>
              <Text>Reporting date: {state.reportingDate}</Text>
              <Text>File version: {state.fileVersion}</Text>
              <Button mt="20px" isDisabled>
                {state.status === 'inProgress' && <Spinner mr="12px" />}
                Delete {state.reportingDate}-v{state.fileVersion}
              </Button>
              {state.status === 'success' && (
                <Alert status="info" mt="12px">
                  <AlertDescription>
                    Deleted {state.reportingDate}-v{state.fileVersion}
                  </AlertDescription>
                </Alert>
              )}
              {state.status === 'error' && (
                <Alert status="error" mt="12px">
                  <AlertDescription>
                    Failed to delete {state.reportingDate}-v{state.fileVersion}
                  </AlertDescription>
                </Alert>
              )}
            </>
          );
        }
        if (reportingDate && fileVersion) {
          return (
            <>
              <Text>Reporting date: {reportingDate}</Text>
              <Text>File version: {fileVersion}</Text>
              <Button
                mt="20px"
                onClick={async () => {
                  const { data: innerData } = await undoPipelineRun({
                    variables: {
                      creditFacilityId,
                      reportingDate,
                      fileVersion,
                    },
                  });
                  setState({
                    operationId: innerData!.undoPipelineRun.response,
                    reportingDate,
                    fileVersion,
                    status: 'inProgress',
                  });
                }}
              >
                Delete {reportingDate}-v{fileVersion}
              </Button>
            </>
          );
        }
        return <Text>No pipeline run found</Text>;
      })()}
    </>
  );
};
